import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';
const TC = '#e65100';
const TB = '#fff3e0';
const TC2 = '#bf360c';

const SAMPLE_SITES = [
  { label: '네이버 뉴스', url: 'https://news.naver.com', query: '오늘의 주요 뉴스 헤드라인을 정리해줘' },
  { label: '한국은행 경제통계', url: 'https://www.bok.or.kr', query: '최신 경제 지표와 금리 정보를 정리해줘' },
  { label: 'Hacker News', url: 'https://news.ycombinator.com', query: '현재 인기 있는 기술 트렌드를 정리해줘' },
  { label: 'GitHub Trending', url: 'https://github.com/trending', query: '현재 트렌딩 프로젝트들을 표로 정리해줘' },
  { label: '공공데이터포털', url: 'https://www.data.go.kr', query: '인기 있는 공공 API와 데이터셋을 정리해줘' },
  { label: 'Product Hunt', url: 'https://www.producthunt.com', query: '오늘의 인기 제품을 정리해줘' },
];

const CRAWL_MODES = [
  { value: 'smart', label: '스마트 크롤링', desc: 'AI가 페이지를 분석하여 핵심 콘텐츠 추출', icon: '🤖' },
  { value: 'full', label: '전체 텍스트', desc: '페이지의 모든 텍스트를 가져옵니다', icon: '📄' },
  { value: 'links', label: '링크 수집', desc: '페이지 내 모든 링크를 수집합니다', icon: '🔗' },
  { value: 'tables', label: '표 데이터 추출', desc: '페이지 내 테이블을 추출합니다', icon: '📊' },
];

const OUTPUT_STYLES = [
  { value: 'summary', label: '요약 정리' },
  { value: 'detailed', label: '상세 정리' },
  { value: 'table', label: '표 형식' },
  { value: 'bullet', label: '리스트 형식' },
];

function CrawlAgent() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('crawl'); // crawl, result, history
  const [url, setUrl] = useState('');
  const [query, setQuery] = useState('');
  const [crawlMode, setCrawlMode] = useState('smart');
  const [outputStyle, setOutputStyle] = useState('summary');
  const [depth, setDepth] = useState(1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [savedList, setSavedList] = useState([]);
  const [refineInput, setRefineInput] = useState('');
  const [multiUrls, setMultiUrls] = useState(false);
  const [urlList, setUrlList] = useState('');
  const resultRef = useRef(null);

  // 크롤링 실행
  const handleCrawl = useCallback(async () => {
    const urls = multiUrls ? urlList.split('\n').map(u => u.trim()).filter(u => u) : [url.trim()];
    if (!urls.length || !urls[0]) { setError('URL을 입력해주세요.'); return; }
    if (!query.trim()) { setError('검색/정리할 내용을 입력해주세요.'); return; }

    setLoading(true);
    setError('');
    setProgress('웹 페이지에 접속하고 있습니다...');

    try {
      const res = await fetch(`${API}/crawl/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          urls,
          query,
          crawl_mode: crawlMode,
          output_style: outputStyle,
          depth,
        }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); setProgress(''); return; }
      setResult(data);
      setMode('result');
    } catch (err) {
      setError('서버 연결 실패: ' + err.message);
    }
    setLoading(false);
    setProgress('');
  }, [url, query, crawlMode, outputStyle, depth, multiUrls, urlList]);

  // 결과 보정 요청
  const handleRefine = useCallback(async () => {
    if (!refineInput.trim() || !result) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/crawl/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original: result.analysis,
          raw_text: result.raw_text,
          instruction: refineInput,
        }),
      });
      const data = await res.json();
      if (data.analysis) {
        setResult(prev => ({ ...prev, analysis: data.analysis }));
        setRefineInput('');
      }
    } catch (err) {
      setError('보정 실패: ' + err.message);
    }
    setLoading(false);
  }, [refineInput, result]);

  // 저장
  const handleSave = useCallback(async () => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/crawl/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: result.title || url,
          url: result.url || url,
          query,
          analysis: result.analysis,
          raw_text: result.raw_text,
        }),
      });
      const data = await res.json();
      if (data.id) alert('저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  }, [result, url, query]);

  // 목록 로드
  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crawl/list`);
      const data = await res.json();
      setSavedList(data);
    } catch { setSavedList([]); }
  }, []);

  // 저장된 결과 불러오기
  const loadSaved = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/crawl/${id}`);
      const data = await res.json();
      setResult(data);
      setUrl(data.url || '');
      setQuery(data.query || '');
      setMode('result');
    } catch (err) {
      alert('불러오기 실패: ' + err.message);
    }
  }, []);

  const deleteSaved = useCallback(async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await fetch(`${API}/crawl/${id}`, { method: 'DELETE' });
      loadHistory();
    } catch {}
  }, [loadHistory]);

  // Word/PDF 다운로드
  const handleDownload = useCallback(async (format) => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/crawl/download/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: result.analysis, title: result.title || '크롤링 결과' }),
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
    } catch (err) {
      alert('다운로드 실패: ' + err.message);
    }
  }, [result]);

  /* ─── 헤더 ─── */
  const header = (
    <div style={{ background: `linear-gradient(135deg, ${TC}, ${TC2})`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          &larr; Hub
        </button>
        <h1 style={{ margin: 0, color: 'white', fontSize: 20, fontWeight: 700 }}>
          <span style={{ marginRight: 8 }}>🕷️</span>크롤링 에이전트
        </h1>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[
          { key: 'crawl', label: '크롤링', icon: '🔍' },
          { key: 'result', label: '결과', icon: '📋' },
          { key: 'history', label: '저장 목록', icon: '📁' },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setMode(tab.key); if (tab.key === 'history') loadHistory(); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: mode === tab.key ? 'white' : 'rgba(255,255,255,0.15)', color: mode === tab.key ? TC : 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  /* ─── 크롤링 입력 ─── */
  const crawlView = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* 왼쪽: URL + 검색어 */}
      <div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: '#333' }}>크롤링 대상 URL</h3>
            <label style={{ fontSize: 12, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={multiUrls} onChange={e => setMultiUrls(e.target.checked)} />
              다중 URL
            </label>
          </div>
          {multiUrls ? (
            <textarea value={urlList} onChange={e => { setUrlList(e.target.value); setError(''); }} rows={5} placeholder={'https://example1.com\nhttps://example2.com\nhttps://example3.com'}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', lineHeight: 1.6 }} />
          ) : (
            <div style={{ position: 'relative' }}>
              <input value={url} onChange={e => { setUrl(e.target.value); setError(''); }} placeholder="https://example.com"
                style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#999' }}>🌐</span>
            </div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>검색/정리 요청</h3>
          <textarea value={query} onChange={e => { setQuery(e.target.value); setError(''); }} rows={4} placeholder="예: 이 사이트의 주요 뉴스를 카테고리별로 정리해줘&#10;예: 가격 정보를 표로 만들어줘&#10;예: 최근 공지사항을 날짜순으로 정리해줘"
            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }} />
        </div>

        {/* 크롤링 모드 */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>크롤링 모드</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {CRAWL_MODES.map(m => (
              <button key={m.value} onClick={() => setCrawlMode(m.value)}
                style={{
                  padding: '10px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s',
                  border: crawlMode === m.value ? `2px solid ${TC}` : '1px solid #e0e0e0',
                  background: crawlMode === m.value ? TB : 'white',
                }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 2 }}>{m.icon} {m.label}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 옵션 */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#555', fontWeight: 600, display: 'block', marginBottom: 6 }}>결과 형식</label>
            <select value={outputStyle} onChange={e => setOutputStyle(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
              {OUTPUT_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, color: '#555', fontWeight: 600, display: 'block', marginBottom: 6 }}>크롤링 깊이</label>
            <select value={depth} onChange={e => setDepth(Number(e.target.value))}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
              <option value={1}>1단계 (현재 페이지)</option>
              <option value={2}>2단계 (하위 링크 포함)</option>
              <option value={3}>3단계 (심층 크롤링)</option>
            </select>
          </div>
        </div>
      </div>

      {/* 오른쪽: 샘플 + 안내 */}
      <div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>샘플 사이트</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {SAMPLE_SITES.map((s, i) => (
              <button key={i} onClick={() => { setUrl(s.url); setQuery(s.query); setMultiUrls(false); setError(''); }}
                style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 12 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.background = TB; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>{s.url}</div>
                </div>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: `linear-gradient(135deg, ${TB}, #ffe0b2)`, borderRadius: 12, padding: 20, border: '1px solid #ffcc80' }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, color: TC }}>사용 팁</h4>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#555', lineHeight: 2 }}>
            <li>구체적인 요청일수록 결과가 좋습니다</li>
            <li><strong>표 데이터 추출</strong> 모드는 가격표, 스펙 비교에 유용합니다</li>
            <li><strong>다중 URL</strong>로 여러 사이트를 한번에 비교 분석 가능</li>
            <li>크롤링 깊이 2~3단계는 하위 페이지까지 탐색합니다</li>
            <li>로그인이 필요한 페이지는 크롤링이 제한될 수 있습니다</li>
          </ul>
        </div>
      </div>

      {/* 실행 버튼 */}
      <div style={{ gridColumn: '1 / -1' }}>
        {error && <div style={{ padding: '10px 16px', borderRadius: 8, background: '#fff3f0', color: '#d32f2f', fontSize: 13, marginBottom: 12, border: '1px solid #ffcdd2' }}>{error}</div>}
        {loading && progress && (
          <div style={{ padding: '12px 16px', borderRadius: 8, background: TB, color: TC, fontSize: 13, marginBottom: 12, border: `1px solid #ffcc80`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" stroke={TC} strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>
            {progress}
          </div>
        )}
        <button onClick={handleCrawl} disabled={loading} style={{
          width: '100%', padding: '14px', borderRadius: 10, border: 'none',
          background: loading ? '#bbb' : `linear-gradient(135deg, ${TC}, #ff8f00)`,
          color: 'white', fontSize: 16, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
          boxShadow: loading ? 'none' : '0 4px 12px rgba(230,81,0,0.3)',
        }}>
          {loading ? (
            <span><svg width="18" height="18" viewBox="0 0 24 24" style={{verticalAlign:'middle',marginRight:8,animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>크롤링 및 분석 중...</span>
          ) : '크롤링 시작'}
        </button>
      </div>
    </div>
  );

  /* ─── 결과 보기 ─── */
  const resultView = result ? (
    <div ref={resultRef}>
      {/* 결과 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#333' }}>{result.title || '크롤링 결과'}</h2>
          <div style={{ fontSize: 12, color: '#888' }}>
            {result.url && <span style={{ marginRight: 12 }}>🌐 {result.url}</span>}
            {result.crawled_pages && <span style={{ marginRight: 12 }}>📄 {result.crawled_pages}페이지 분석</span>}
            {result.text_length && <span>📝 {result.text_length.toLocaleString()}자 추출</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${TC}`, background: 'white', color: TC, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>저장</button>
          <button onClick={() => handleDownload('word')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #1565c0', background: 'white', color: '#1565c0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Word</button>
          <button onClick={() => handleDownload('pdf')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c62828', background: 'white', color: '#c62828', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>PDF</button>
          <button onClick={() => setMode('crawl')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ccc', background: 'white', color: '#666', fontSize: 13, cursor: 'pointer' }}>새 크롤링</button>
        </div>
      </div>

      {/* 추출된 링크 요약 */}
      {result.links && result.links.length > 0 && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#555', cursor: 'pointer' }}
            onClick={e => { const el = e.currentTarget.nextSibling; el.style.display = el.style.display === 'none' ? 'block' : 'none'; }}>
            🔗 수집된 링크 ({result.links.length}개) <span style={{ fontSize: 11, color: '#999' }}>클릭하여 펼치기</span>
          </h4>
          <div style={{ display: 'none', maxHeight: 200, overflowY: 'auto' }}>
            {result.links.slice(0, 50).map((link, i) => (
              <div key={i} style={{ fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                  {link.text || link.url}
                </a>
                {link.text && <span style={{ color: '#999', marginLeft: 8, fontSize: 11 }}>{link.url}</span>}
              </div>
            ))}
            {result.links.length > 50 && <div style={{ fontSize: 12, color: '#999', padding: 4 }}>... 외 {result.links.length - 50}개</div>}
          </div>
        </div>
      )}

      {/* AI 분석 결과 */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 16, border: '1px solid #e0e0e0' }}>
        <div style={{ fontSize: 14, color: '#333', lineHeight: 1.8 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            table: ({node, ...props}) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '12px 0' }} {...props} />,
            th: ({node, ...props}) => <th style={{ padding: '10px 14px', background: TC, color: 'white', textAlign: 'left', borderBottom: `2px solid ${TC2}`, fontSize: 13 }} {...props} />,
            td: ({node, ...props}) => <td style={{ padding: '9px 14px', borderBottom: '1px solid #e0e0e0', fontSize: 13 }} {...props} />,
            a: ({node, ...props}) => <a style={{ color: '#1565c0' }} target="_blank" rel="noopener noreferrer" {...props} />,
            h1: ({node, ...props}) => <h1 style={{ fontSize: 20, color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: 8, marginTop: 24 }} {...props} />,
            h2: ({node, ...props}) => <h2 style={{ fontSize: 17, color: '#444', marginTop: 20 }} {...props} />,
            h3: ({node, ...props}) => <h3 style={{ fontSize: 15, color: '#555', marginTop: 16 }} {...props} />,
            blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: `3px solid ${TC}`, margin: '12px 0', padding: '8px 16px', background: TB, borderRadius: '0 8px 8px 0' }} {...props} />,
          }}>{result.analysis}</ReactMarkdown>
        </div>
      </div>

      {/* 추가 요청 */}
      <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#666' }}>추가 요청 / 보정</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={refineInput} onChange={e => setRefineInput(e.target.value)} placeholder="예: 표 형식으로 바꿔줘, 더 자세하게 정리해줘, 영어로 번역해줘..."
            style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
            onKeyDown={e => { if (e.key === 'Enter') handleRefine(); }} />
          <button onClick={handleRefine} disabled={loading || !refineInput.trim()}
            style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: refineInput.trim() ? TC : '#ccc', color: 'white', fontSize: 13, fontWeight: 600, cursor: refineInput.trim() ? 'pointer' : 'default' }}>
            {loading ? '처리 중...' : '보정'}
          </button>
        </div>
      </div>

      {/* 원본 텍스트 (접기) */}
      {result.raw_text && (
        <div style={{ marginTop: 16, background: '#fafafa', borderRadius: 12, padding: 16, border: '1px solid #e0e0e0' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: '#888', cursor: 'pointer' }}
            onClick={e => { const el = e.currentTarget.nextSibling; el.style.display = el.style.display === 'none' ? 'block' : 'none'; }}>
            📄 원본 추출 텍스트 <span style={{ fontSize: 11 }}>클릭하여 펼치기</span>
          </h4>
          <pre style={{ display: 'none', marginTop: 10, fontSize: 12, color: '#666', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflowY: 'auto', lineHeight: 1.6 }}>
            {result.raw_text.slice(0, 10000)}{result.raw_text.length > 10000 ? '\n... (이하 생략)' : ''}
          </pre>
        </div>
      )}
    </div>
  ) : (
    <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
      크롤링 결과가 없습니다. 먼저 크롤링을 실행해주세요.
    </div>
  );

  /* ─── 히스토리 ─── */
  const historyView = (
    <div>
      <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#333' }}>저장된 크롤링 결과</h3>
      {savedList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 12, border: '1px solid #e0e0e0', color: '#999' }}>
          저장된 결과가 없습니다.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {savedList.map(item => (
            <div key={item.id} style={{ background: 'white', borderRadius: 12, padding: '16px 20px', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.boxShadow = 'none'; }}
              onClick={() => loadSaved(item.id)}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 4 }}>{item.title || '(제목 없음)'}</div>
                <div style={{ fontSize: 12, color: '#999' }}>
                  <span style={{ marginRight: 12 }}>{item.url}</span>
                  {item.created_at && <span>{new Date(item.created_at).toLocaleString('ko-KR')}</span>}
                </div>
              </div>
              <button onClick={e => { e.stopPropagation(); deleteSaved(item.id); }}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e0e0e0', background: 'white', color: '#999', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#e53935'; e.currentTarget.style.color = '#e53935'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.color = '#999'; }}>
                삭제
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {header}
      <div style={{ maxWidth: 1300, margin: '0 auto', padding: 20 }}>
        {mode === 'crawl' && crawlView}
        {mode === 'result' && resultView}
        {mode === 'history' && historyView}
      </div>
    </div>
  );
}

export default CrawlAgent;
